import { useCallback, useMemo, useRef, useState } from "react";
import "./App.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import ResponsiveNavbar from "./components/ResponsiveNavbar/ResponsiveAppBar";
import NotFound from "./pages/NotFound";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.css";
import Messages from "./components/Messages/Messages";
import { ChatPopup } from "./components/ChatPopup/ChatPopup";
import { UserProfile } from "./components/UserProfile/UserProfile";
import { AuthContext, EnterpriseType, UserDetailsContext } from "./contexts";
import { TalkJsContext, S3CredentialsContext } from "./contexts";
import { useTalk } from "./hooks/useTalk";
import { useGetUser } from "./hooks/useGetUser";
import PageLayout from "./components/PageLayout/PageLayout";
import Mixpanel from "./utils/mixpanel";
import PrivacyPolicy from "./pages/Legal/Privacy";
import CreatorAgreement from "./pages/Legal/CreatorAgreement";
import TermsOfUse from "./pages/Legal/TermsOfUse";
import EnterpriseAddendum from "./pages/Legal/EnterpriseAddendum";
import WelcomePage from "./pages/Welcome/WelcomePage";
import Footer from "./components/Footer/Footer";
import Billing from "./pages/Billing/Billing";
import { getStorageUsage } from "./services/storageService";
import Success from "./pages/Billing/Success";
import PaymentCancel from "./pages/Billing/PaymentCancel";
import {
  Enterprise,
  EnterpriseDetails,
  GetEnterprisesForUserResponse,
  Role,
} from "./proto/api_pb";
import { SubscriptionTier } from "./proto/api_pb";
import { SubscriptionState } from "./proto/api_pb";
import { SignUpSplash } from "./pages/SignupSplash/SignupSplash";
import useGetValidAccessToken from "./hooks/useGetValidAccessToken";
import PricingTable from "./pages/PricingTable/PricingTable";
import { useGetAllUsers } from "./hooks/useGetAllUsers";
import DefaultPage from "./pages/DefaultPage";
import { useS3Credentials } from "./hooks/useS3Credentials";
import { FileUploadStatusCard } from "./components/FileUploadStatusCard/FileUploadStatusCard";
import {
  FolderUploadInfo,
  UploadCardStatusInfo,
} from "./components/FileUploadStatusCard/types";
import { UploadedFileInfo } from "./components/FileUploadStatusCard/types";
import { isEnterprise } from "./theme";
import { SharedContentPage } from "./pages/SharedContentPage/SharedContentPage";
import ShareManagement from "./pages/ShareManagement/ShareManagement";
import { hotjar } from "react-hotjar";
import InitialSettings from "./pages/InitialSettings/InitialSettings";
import { TracksPage } from "./pages/Tracks/TracksPage";
import Loading from "./pages/Loading";
import { ExplorePage } from "./pages/ExplorePage/ExplorePage";
import { ProfilePage } from "./pages/ProfilePage/ProfilePage";
import { getPaymentMethod } from "./components/TrackComponents/TrackHandler/trackHandlers";
import { Projects } from "./pages/Projects/Projects";
import { SelectEnterprise } from "./pages/Enterprise/SelectEnterprise";
import { routesWithoutFooter } from "./components/constants";
import { routesWithoutNavbar } from "./components/constants";
import { TeamManagement } from "./pages/TeamManagement/TeamManagement";
import { handleLastActive, checkEnterpriseAddendumAccepted } from "./services/userService";
import { getAllUserEnterprises } from "./services/enterpriseService";
import { OpenFoldersListType } from "./pages/Tracks/types";
import { DrawerContainer } from "./components/TracksPageComponents/styles";
import { Drawer } from "./components/TracksPageComponents/Drawer";
import { hiddenDrawerRoutes } from "./helper";
import ConsentModal from "./components/Modal/ConsentModal";
import SharedFolderPage from "./pages/SharedContentPage/SharedFolderPage";
import { SharedProjectsPage } from "./pages/SharedContentPage/SharedProjectsPage";
import Dashboard from "./pages/DashboardPage/DashboardPage";
import { stateMap, tierMap } from "./pages/Billing/types";
import { fetchTracks } from "./services/trackService";
import { GetMyTracksResponse } from "./proto/api_pb";
import { TrackFilterParams } from "./pages/MyTracks/types";

const App: React.FC = () => {
  const url: string = window.location.pathname;
  const { loginWithRedirect, getIdTokenClaims, isAuthenticated, isLoading } =
    useAuth0();
  const [userId, setUserId] = useState("");
  const [storageUsage, setStorageUsage] = useState<number | null>(null);
  const [successReg, setSuccessReg] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean>(false);
  const [uploadingFilesDetailsList, setUploadingFilesDetailsList] = useState<
    UploadedFileInfo[]
  >([]);
  const [uploadingCardsStatusList, setUploadingCardsStatusList] = useState<
    UploadCardStatusInfo[]
  >([]);
  const [conversationId, setConversationId] = useState<string>("");
  const [conversationSubject, setConversationSubject] = useState<string>("");
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  const [enterprises, setEnterprises] = useState<EnterpriseDetails[]>([]);
  const [allEnterprisesInfo, setAllEnterprisesInfo] = useState<Enterprise[]>(
    []
  );
  const [folderUploadInfo, setFolderUploadInfo] = useState<FolderUploadInfo>();
  const [selectedEnterprise, setSelectedEnterprise] =
    useState<EnterpriseType>();
  const [openFoldersList, setOpenFoldersList] = useState<OpenFoldersListType[]>(
    []
  );

  const [isProcessing, setIsProcessing] = useState(false);
  const [showEnterpriseAddendum, setShowEnterpriseAddendum] = useState(false);
  const [accessTokenForModal, setAccessTokenForModal] = useState("");
  const addendumChecked = useRef(false);
  const sessionStartRef = useRef<number>(0);
  const cachedTokenRef = useRef<string>("");
  const hasTrackedLoginRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { getValidAccessToken } = useGetValidAccessToken();
  const { allUsers, featuredUsers } = useGetAllUsers();
  const [profileUpdated] = useState(false);

  useEffect(() => {
    hotjar.initialize({
      id: parseInt(process.env.REACT_APP_HOTJAR_SITE_ID!, 10),
      sv: parseInt(process.env.REACT_APP_HOTJAR_SITE_VERSION!, 10),
    });
  }, []);

  const hideNavbar = routesWithoutNavbar.some((route) =>
    location.pathname.includes(route)
  );

  const hideFooter = routesWithoutFooter.some((route) =>
    location.pathname.includes(route)
  );

  const isPublicPage =
    location.pathname === "/privacy-policy" ||
    location.pathname === "/creator-agreement" ||
    location.pathname === "/enterprise-addendum" ||
    location.pathname === "/terms-of-use" ||
    location.pathname === "/signup" ||
    location.pathname.includes("/share/") ||
    location.pathname.includes("explore") ||
    location.pathname.includes("user-profile");

  const shouldShowDrawer = !hiddenDrawerRoutes.includes(location.pathname);

  const handleSuccess = () => {
    setSuccessReg(true);
  };

  useEffect(() => {
    if (location.pathname === "/login") {
      loginWithRedirect({
        appState: { returnTo: "/my-tracks" },
      });
    }
    if (location.pathname === "/signup") {
      loginWithRedirect({
        appState: { returnTo: "/my-tracks" },
        authorizationParams: { screen_hint: "signup" },
      });
    }
  }, [location.pathname, loginWithRedirect]);

  // This is because we need to add every event on the page to mixpanel
  useEffect(() => {
    Mixpanel.track(window.location.pathname);
  }, [navigate]);

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        const idToken = await getIdTokenClaims();
        if (idToken && idToken.sub !== userId) {
          setUserId(idToken.sub);
        }
      }
    })();
  }, [getIdTokenClaims, isAuthenticated, userId]);

  const getStorageUsed = useCallback(async () => {
    const accessToken = await getValidAccessToken();
    const storageUse = await getStorageUsage(
      userId,
      accessToken,
      selectedEnterprise?.id
    );
    typeof storageUse === "number"
      ? setStorageUsage(storageUse)
      : setStorageUsage(0);
  }, [getValidAccessToken, selectedEnterprise?.id, userId]);

  const getMostRecentTrackTimestamp = useCallback(async (): Promise<
    string | null
  > => {
    try {
      const accessToken = await getValidAccessToken();
      const trackFilterParams: TrackFilterParams = {
        userIds: [userId],
        sortBy: "", // Sort by creation date
        isAscending: false, // Most recent first
        genreList: [],
        artistList: [],
        collaboratorsList: [],
        TrackPrivacy: "",
        page: 0,
        limit: 1, // Only get the most recent track
        bpmStartValue: 0,
        bpmEndValue: 500,
        status: "",
        isCompletedTracks: false,
      };

      const response = await fetchTracks(
        accessToken,
        trackFilterParams,
        "",
        undefined,
        undefined,
        undefined,
        undefined
      );

      if (response instanceof GetMyTracksResponse) {
        const tracksList = response.getTracksList();
        if (tracksList.length > 0) {
          const mostRecentTrackResponse = tracksList[0];
          const track = mostRecentTrackResponse.getTrack();
          if (track) {
            return track.getTimestamp().toString();
          }
        }
      }
      return null;
    } catch (error) {
      console.error("Error fetching most recent track:", error);
      return null;
    }
  }, [getValidAccessToken, userId]);

  const { getUserResponse, getUserError, isUserLoading } = useGetUser(
    userId,
    profileUpdated
  );
  const { s3Credentials: credentials } = useS3Credentials(userId);

  useEffect(() => {
    const handleGetAllEnterprisesInfo = async () => {
      if (getUserResponse && isAuthenticated) {
        const enterpriseIds = getUserResponse
          .getEnterpriseDetailsList()
          .map((enterpriseDetail) => enterpriseDetail.getEnterpriseId());
        const t = await getValidAccessToken();
        const allEnterprisesInfo = await getAllUserEnterprises(
          t,
          enterpriseIds
        );
        console.log("All Enterprises Info:", allEnterprisesInfo);
        if (allEnterprisesInfo instanceof GetEnterprisesForUserResponse) {
          setAllEnterprisesInfo(allEnterprisesInfo.getEnterpriseList());
        }
      }
    };
    handleGetAllEnterprisesInfo();
  }, [getUserResponse, getValidAccessToken, isAuthenticated]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoading && !isAuthenticated && !isPublicPage) {
        navigate("/welcome");
        return;
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, isPublicPage, isLoading, navigate]);

  useEffect(() => {
    if (
      getUserResponse?.getUsername() === "" ||
      getUserResponse?.getUsername().includes("dummy_ommuse")
    ) {
      navigate("initial-setting");
      return;
    }
  }, [getUserResponse, navigate]);

  useEffect(() => {
    const runEffect = async () => {
      if (!isLoading) {
        if (isAuthenticated && getUserError && !isPublicPage && !successReg) {
          navigate("/initial-setting");
        } else if (getUserResponse) {
          try {
            const mostRecentTrackTimestamp =
              await getMostRecentTrackTimestamp();
            if (getUserResponse.getUuid().length > 0 && storageUsage !== null) {
              const uuid = getUserResponse.getUuid();
              const aliasKey = `mp_alias_done:${uuid}`;
              if (!localStorage.getItem(aliasKey)) {
                Mixpanel.alias(uuid); // merge current anon -> uuid
                localStorage.setItem(aliasKey, "1");
              }
              // Always identify on authenticated sessions
              Mixpanel.identify(uuid);
              Mixpanel.people.set({
                first_name: getUserResponse.getFirstName(),
                last_name: getUserResponse.getLastName(),
                username: getUserResponse.getUsername(),
                $email: getUserResponse.getEmail(),
                subscription_tier:
                  tierMap[getUserResponse.getSubscriptionTier()],
                subscription_state:
                  stateMap[getUserResponse.getSubscriptionState()],
              });
              const fixedStorage = Number(storageUsage.toFixed(3));
              if (
                selectedEnterprise?.name
                  ? selectedEnterprise.name !== "ommuse"
                  : false
              ) {
                const enterpriseName = selectedEnterprise?.name
                  ?.toLowerCase()
                  .replace(/\s+/g, "_");
                Mixpanel.people.set({
                  [`storage_usage_${enterpriseName}`]: fixedStorage,
                });
              } else {
                Mixpanel.people.set({ storage_usage: fixedStorage });
              }

              if (mostRecentTrackTimestamp) {
                Mixpanel.people.set({
                  most_recent_track_timestamp: mostRecentTrackTimestamp,
                });
              }
            }

            console.log("storageUsage", storageUsage);
            getStorageUsed();

            const stripeCustomerId = getUserResponse.getStripeCustomerId();
            const currentPath = window.location.pathname;
            const enterpriseName = selectedEnterprise?.name;
            const isInitialPath = [
              "/",
              "/initial-setting",
              "/signup",
              "/welcome",
            ].includes(currentPath);

            if (!stripeCustomerId) {
              if (enterpriseName === "ommuse") {
                navigate("/pricing-table");
              } else if (isEnterprise && isInitialPath) {
                navigate("/my-tracks");
              }
            } else if (isInitialPath) {
              navigate("/my-tracks");
            }
          } catch (error) {
            console.error("Error fetching most recent track timestamp:", error);
          }
        }
      }
    };

    runEffect();
  }, [
    getStorageUsed,
    getMostRecentTrackTimestamp,
    storageUsage,
    getUserError,
    getUserResponse,
    isAuthenticated,
    isLoading,
    isPublicPage,
    navigate,
    selectedEnterprise,
    successReg,
  ]);

  const [chatOpen, setChatOpen] = useState(false);

  const handleChatClick = () => {
    setChatOpen(!chatOpen);
    Mixpanel.track(chatOpen ? "messages_popup_close" : "messages_popup_open");
  };

  const {
    isChatLoading,
    session,
    setInbox,
    setParticipants,
    createConversation,
    autoCreateChat,
    setAutoCreateChat,
    unreadMessages,
  } = useTalk(userId);

  const authContextValue = useMemo(() => ({ userId }), [userId]);
  const userDetailsContextValue = useMemo(
    () => ({
      userName: getUserResponse?.getUsername() ?? "",
      fullName:
        getUserResponse?.getFirstName() + " " + getUserResponse?.getLastName(),
      connectedWallet: getUserResponse?.getWalletAddressesList()[0] ?? "",
      storageUsage: storageUsage ?? 0,
      stripeCustomerId: getUserResponse?.getStripeCustomerId() ?? "",
      subscriptionState:
        getUserResponse?.getSubscriptionState() ??
        SubscriptionState.SUBSCRIPTION_STATE_UNSPECIFIED,
      subscriptionTier:
        getUserResponse?.getSubscriptionTier() ??
        SubscriptionTier.SUBSCRIPTION_TIER_UNSPECIFIED,
      allUsers: allUsers,
      featuredUsers: featuredUsers,
      hasPaymentMethod: hasPaymentMethod,
      enterprise: selectedEnterprise,
      role: selectedEnterprise?.role ?? "",
      isEnterpriseAccount: selectedEnterprise?.name
        ? selectedEnterprise.name !== "ommuse"
        : false,
      enterprises: getUserResponse?.getEnterpriseDetailsList() ?? [],
    }),
    [
      getUserResponse,
      storageUsage,
      allUsers,
      featuredUsers,
      hasPaymentMethod,
      selectedEnterprise,
    ]
  );

  const s3CredentialsContextValue = useMemo(
    () => ({
      awsAccessKeyId: credentials?.awsAccessKeyId,
      awsSecretAccessKey: credentials?.awsSecretAccessKey,
      awsSessionToken: credentials?.awsSessionToken,
      expiry: credentials?.expiry,
    }),
    [credentials]
  );

  useEffect(() => {
    if (isAuthenticated && userId) {
      const handleUseEffect = async () => {
        if (!userId) return;
        const token = await getValidAccessToken();
        const getMethod = await getPaymentMethod(token, userId);
        if (getMethod) setHasPaymentMethod(getMethod.getHasPaymentMethod());
      };
      handleUseEffect();
    }
  }, [getValidAccessToken, isAuthenticated, userId]);

  useEffect(() => {
    const storedEnterpriseJSON = sessionStorage.getItem("enterprise");
    if (getUserResponse?.getUserId()) {
      setEnterprises(getUserResponse.getEnterpriseDetailsList());
      if (getUserResponse.getEnterpriseDetailsList().length === 1) {
        const e = getUserResponse.getEnterpriseDetailsList()[0];
        const newEnterprise: EnterpriseType = {
          id: e.getEnterpriseId(),
          name: e.getEnterpriseName(),
          image:
            allEnterprisesInfo.length > 0
              ? (allEnterprisesInfo[0].getImage() ?? "")
              : "",
          role: e.getRole(),
          storageLimit:
            allEnterprisesInfo.length > 0
              ? (allEnterprisesInfo[0].getStorageLimit() ?? 0)
              : 0,
          masteringCount: allEnterprisesInfo[0]?.getMasteringCount?.() ?? 0,
        };
        sessionStorage.setItem("enterprise", JSON.stringify(newEnterprise));
      }
    }
    if (!storedEnterpriseJSON) {
      if (
        window.location.pathname !== "/initial-setting" &&
        window.location.pathname !== "/pricing-table" &&
        !isLoading &&
        isAuthenticated &&
        enterprises.length > 0 &&
        !isPublicPage
      ) {
        navigate("/select-enterprise");
        return;
      }
    } else {
      const e: EnterpriseType = JSON.parse(storedEnterpriseJSON);
      const matchingEnterprise: EnterpriseDetails | undefined =
        enterprises.find((ent) => ent.getEnterpriseId() === e.id);
      const matchingEnterpriseForImage = allEnterprisesInfo.find(
        (ent) => ent.getId() === e.id
      );
      if (matchingEnterprise) {
        const UpdatedEnterprise: EnterpriseType = {
          id: e.id,
          name: e.name,
          image:
            allEnterprisesInfo.length > 0
              ? (matchingEnterpriseForImage?.getImage() ?? "")
              : "",
          role: matchingEnterprise.getRole(),
          storageLimit:
            allEnterprisesInfo.length > 0
              ? (matchingEnterpriseForImage?.getStorageLimit() ?? 0)
              : 0,
          masteringCount:
            matchingEnterpriseForImage?.getMasteringCount?.() ?? 0,
        };
        sessionStorage.setItem("enterprise", JSON.stringify(UpdatedEnterprise));
        setSelectedEnterprise(UpdatedEnterprise);
      } else setSelectedEnterprise(e);
    }
  }, [
    allEnterprisesInfo,
    enterprises,
    enterprises.length,
    getUserResponse,
    getValidAccessToken,
    isAuthenticated,
    isLoading,
    isPublicPage,
    navigate,
    userId,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !selectedEnterprise || !userId) return;

    sessionStartRef.current = Date.now();

    const sendActivity = async (sessionDurationSeconds: number) => {
      const token = await getValidAccessToken();
      cachedTokenRef.current = token;
      handleLastActive(userId, token, selectedEnterprise.id, sessionDurationSeconds);
    };

    // Only fire the login ping once per authenticated session, not on every effect re-run
    if (!hasTrackedLoginRef.current) {
      hasTrackedLoginRef.current = true;
      sendActivity(0);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
        // Use cached token — synchronous path, no await
        handleLastActive(userId, cachedTokenRef.current, selectedEnterprise.id, elapsed);
      } else {
        sessionStartRef.current = Date.now();
      }
    };

    const handleBeforeUnload = () => {
      const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      // Use cached token — beforeunload cannot await async calls
      handleLastActive(userId, cachedTokenRef.current, selectedEnterprise.id, elapsed);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [getValidAccessToken, isAuthenticated, selectedEnterprise, userId]);

  useEffect(() => {
    if (
      getUserResponse?.getStripeCustomerId() === "" &&
      selectedEnterprise?.name === "ommuse"
    ) {
      navigate("/pricing-table");
      return;
    }
  }, [enterprises.length, getUserResponse, selectedEnterprise, navigate]);

  useEffect(() => {
    Mixpanel.track("App Open", {
      timestamp: new Date().toISOString(),
      userName: getUserResponse?.getUsername() || "",
    });
  }, [getUserResponse]);

  // Unix timestamp for April 3, 2025 00:00:00 UTC — addendum applies to members added from this date onward
  const ENTERPRISE_ADDENDUM_CUTOFF = 1743638400;

  useEffect(() => {
    if (
      addendumChecked.current ||
      !isAuthenticated ||
      !userId ||
      !selectedEnterprise ||
      selectedEnterprise.name === "ommuse"
    ) return;

    const enterpriseDetail = getUserResponse
      ?.getEnterpriseDetailsList()
      .find((e) => e.getEnterpriseId() === selectedEnterprise.id);
    const role = Number(enterpriseDetail?.getRole() ?? selectedEnterprise.role);
    const dateAdded = enterpriseDetail?.getDateAdded() ?? 0;
    if (role !== Role.ROLE_ADMIN || dateAdded < ENTERPRISE_ADDENDUM_CUTOFF) {
      return;
    }

    addendumChecked.current = true;
    (async () => {
      const token = await getValidAccessToken();
      const accepted = await checkEnterpriseAddendumAccepted(token);
      if (!accepted) {
        setAccessTokenForModal(token);
        setShowEnterpriseAddendum(true);
      }
    })();
  }, [isAuthenticated, userId, selectedEnterprise, getUserResponse, getValidAccessToken]);

  if (
    !isPublicPage &&
    (isLoading ||
      (isAuthenticated &&
        !selectedEnterprise &&
        location.pathname !== "/select-enterprise" &&
        window.location.pathname !== "/initial-setting"))
  ) {
    return <Loading />;
  }

  return (
    <>
      <ConsentModal
        open={showEnterpriseAddendum}
        accessToken={accessTokenForModal}
        onAccept={() => setShowEnterpriseAddendum(false)}
      />
      <AuthContext.Provider value={authContextValue}>
        <UserDetailsContext.Provider value={userDetailsContextValue}>
          <S3CredentialsContext.Provider value={s3CredentialsContextValue}>
            <TalkJsContext.Provider
              value={{
                handleChatClick,
                setParticipants,
                createConversation,
                setAutoCreateChat,
                conversationId,
                setConversationId,
                conversationSubject,
                setConversationSubject,
                isChatOpen,
                setIsChatOpen,
              }}
            >
              <div
                style={{
                  position: "relative",
                  minHeight: !hideFooter ? "100vh" : 0,
                  display: "flex",
                }}
              >
                {shouldShowDrawer && (
                  <DrawerContainer>
                    <Drawer
                      currentUser={getUserResponse}
                      isChatLoading={isChatLoading}
                      session={session}
                      setInbox={setInbox}
                      setParticipants={setParticipants}
                      createConversation={createConversation}
                      autoCreateChat={autoCreateChat}
                    />
                  </DrawerContainer>
                )}
                <div style={{ flex: 1, paddingBottom: !hideFooter ? 275 : 0 }}>
                  {isUploading && (
                    <FileUploadStatusCard
                      uploadingFilesDetailsList={uploadingFilesDetailsList}
                      setIsUploading={setIsUploading}
                      uploadingCardsStatusList={uploadingCardsStatusList}
                      setUploadingCardsStatusList={setUploadingCardsStatusList}
                      isProcessing={isProcessing}
                      folderUploadInfo={folderUploadInfo}
                    />
                  )}
                  {!hideNavbar && (
                    <ResponsiveNavbar currentUser={getUserResponse} />
                  )}
                  {url !== "/" &&
                    url !== "/messages" &&
                    isAuthenticated &&
                    !location.pathname.includes("/share/") &&
                    !hideFooter && (
                      <ChatPopup
                        chatOpen={chatOpen}
                        handleChatClick={handleChatClick}
                        unreadMessages={unreadMessages}
                      >
                        <Messages
                          isSmallScreen={true}
                          autoCompleteOptions={allUsers}
                          isChatLoading={isChatLoading}
                          session={session}
                          setInbox={setInbox}
                          setParticipants={setParticipants}
                          createConversation={createConversation}
                          autoCreateChat={autoCreateChat}
                        />
                      </ChatPopup>
                    )}
                  <PageLayout hideNavbar={hideNavbar}>
                    <Routes>
                      <Route
                        path="/share-management"
                        element={<ShareManagement />}
                      />
                      <Route path="/not-found" element={<NotFound />} />
                      <Route
                        path="/team-management"
                        element={
                          <TeamManagement currentUser={getUserResponse} />
                        }
                      />
                      <Route
                        path="/privacy-policy"
                        element={<PrivacyPolicy />}
                      />
                      <Route
                        path="/creator-agreement"
                        element={<CreatorAgreement />}
                      />
                      <Route
                        path="/enterprise-addendum"
                        element={<EnterpriseAddendum />}
                      />
                      <Route path="/terms-of-use" element={<TermsOfUse />} />
                      <Route
                        path="/explore"
                        element={
                          <ExplorePage session={session} setInbox={setInbox} />
                        }
                      />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route
                        path="/my-tracks"
                        element={
                          <TracksPage
                            setUploadingFilesDetailsList={
                              setUploadingFilesDetailsList
                            }
                            setIsUploading={setIsUploading}
                            setUploadingCardsStatusList={
                              setUploadingCardsStatusList
                            }
                            session={session}
                            setInbox={setInbox}
                            setIsUploadProcessing={setIsProcessing}
                            setFolderUploadInfo={setFolderUploadInfo}
                            setOpenFoldersList={setOpenFoldersList}
                            getStorageUsed={getStorageUsed}
                          />
                        }
                      />
                      <Route
                        path={`/share/:id`}
                        element={
                          <SharedContentPage
                            currentUser={getUserResponse}
                            session={session}
                            setInbox={setInbox}
                          />
                        }
                      />
                      <Route
                        path={`/share/:name/:id`}
                        element={
                          <SharedFolderPage isUserLoading={isUserLoading} />
                        }
                      />
                      <Route
                        path={`/share/project/:name/:id`}
                        element={
                          <SharedFolderPage isUserLoading={isUserLoading} />
                        }
                      />
                      <Route
                        path={`/share/projects/:id`}
                        element={<SharedProjectsPage />}
                      />
                      <Route
                        path="/initial-setting"
                        element={
                          <InitialSettings handleSuccess={handleSuccess} />
                        }
                      />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route
                        path="/messages"
                        element={
                          <Messages
                            autoCompleteOptions={allUsers}
                            isChatLoading={isChatLoading}
                            session={session}
                            setInbox={setInbox}
                            setParticipants={setParticipants}
                            createConversation={createConversation}
                            autoCreateChat={autoCreateChat}
                          />
                        }
                      />
                      <Route
                        path="/messages/:conversationId/:conversationSubject"
                        element={
                          <Messages
                            autoCompleteOptions={allUsers}
                            isChatLoading={isChatLoading}
                            session={session}
                            setInbox={setInbox}
                            setParticipants={setParticipants}
                            createConversation={createConversation}
                            autoCreateChat={autoCreateChat}
                          />
                        }
                      />
                      <Route
                        path="/user-profile/:usr"
                        element={
                          <UserProfile session={session} setInbox={setInbox} />
                        }
                      />
                      <Route
                        path="*"
                        element={<Navigate to="/not-found" replace={true} />}
                      />
                      {!isEnterprise && (
                        <Route
                          path="/pricing-table"
                          element={<PricingTable />}
                        />
                      )}
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/logout" element={<WelcomePage />} />
                      <Route
                        path="/select-enterprise"
                        element={
                          <SelectEnterprise
                            enterprises={enterprises}
                            allEnterprisesInfo={allEnterprisesInfo}
                          />
                        }
                      />
                      <Route
                        path="/projects"
                        element={
                          <Projects
                            session={session}
                            setInbox={setInbox}
                            openFoldersList={openFoldersList}
                            setOpenFoldersList={setOpenFoldersList}
                          />
                        }
                      />
                      <Route
                        path="/project/:name/:id"
                        element={
                          <TracksPage
                            setUploadingFilesDetailsList={
                              setUploadingFilesDetailsList
                            }
                            setIsUploading={setIsUploading}
                            setUploadingCardsStatusList={
                              setUploadingCardsStatusList
                            }
                            session={session}
                            setInbox={setInbox}
                            setIsUploadProcessing={setIsProcessing}
                            openFoldersList={openFoldersList}
                            setOpenFoldersList={setOpenFoldersList}
                            setFolderUploadInfo={setFolderUploadInfo}
                            getStorageUsed={getStorageUsed}
                          />
                        }
                      />
                      <Route
                        path="/project/:name/:id/folder/:folderId"
                        element={
                          <TracksPage
                            setUploadingFilesDetailsList={
                              setUploadingFilesDetailsList
                            }
                            setIsUploading={setIsUploading}
                            setUploadingCardsStatusList={
                              setUploadingCardsStatusList
                            }
                            session={session}
                            setInbox={setInbox}
                            setIsUploadProcessing={setIsProcessing}
                            openFoldersList={openFoldersList}
                            setOpenFoldersList={setOpenFoldersList}
                            setFolderUploadInfo={setFolderUploadInfo}
                            getStorageUsed={getStorageUsed}
                          />
                        }
                      />
                      <Route path="/" element={<DefaultPage />} />
                      {!isEnterprise && (
                        <Route path="/billing" element={<Billing />} />
                      )}
                      {isEnterprise &&
                        selectedEnterprise?.role !== undefined &&
                        Number(selectedEnterprise.role) === Role.ROLE_ADMIN && (
                          <Route path="/billing" element={<Billing />} />
                        )}
                      <Route path="/success" element={<Success />} />
                      <Route
                        path="/payment-cancel"
                        element={<PaymentCancel />}
                      />
                      <Route path="/signup-splash" element={<SignUpSplash />} />
                      <Route path="/signup" element={<WelcomePage />} />
                      <Route path="/login"></Route>
                    </Routes>
                  </PageLayout>
                </div>
                {!hideFooter && <Footer />}
              </div>
            </TalkJsContext.Provider>
          </S3CredentialsContext.Provider>
        </UserDetailsContext.Provider>
      </AuthContext.Provider>
    </>
  );
};

export default App;
